import { TextLinkButton } from 'components/buttons/TextLinkButton';
import { FilterDropdownButton } from 'components/filters/FilterDropdownButton';
import { FilterChipRow, FilterChipDef } from 'components/filters/FilterChipRow';
import {
  FilterSection,
  FilterSectionTitle,
  FilterOption,
} from 'components/filters/FilterElements';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { TemplateType } from 'models/item';
import { formatItemTemplate } from 'utils/formatItemTemplate';
import { ContentIntelligenceFilters as FilterType, DifficultyLevel } from './types';

interface ContentIntelligenceFiltersProps {
  filters: FilterType;
  onFiltersChange: (filters: FilterType) => void;
}

interface ContentIntelligenceFilterChipsProps {
  filters: FilterType;
  onFiltersChange: (filters: FilterType) => void;
  isLoading?: boolean;
}

const DIFFICULTY_LEVELS: { value: DifficultyLevel; label: string }[] = [
  { value: 'very_hard', label: 'Very Hard' },
  { value: 'hard', label: 'Hard' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'easy', label: 'Easy' },
  { value: 'very_easy', label: 'Very Easy' },
];

const ITEM_TYPES: TemplateType[] = [
  'question_and_answers',
  'instructional_contents',
  'vocabulary',
  'associations',
  'sequences',
  'regions',
  'free_responses',
  'passages',
];

export const ContentIntelligenceFilters = ({ filters, onFiltersChange }: ContentIntelligenceFiltersProps) => {
  const { t } = useTranslation();

  const difficultyFilters = filters.difficultyLevel || [];
  const templateFilters = filters.template || [];

  const activeFilterCount = difficultyFilters.length + templateFilters.length;

  const handleDifficultyFilterChange = (value: DifficultyLevel, checked: boolean) => {
    const newDifficultyFilters = checked
      ? [...difficultyFilters, value]
      : difficultyFilters.filter((f) => f !== value);
    onFiltersChange({
      ...filters,
      difficultyLevel: newDifficultyFilters.length > 0 ? newDifficultyFilters : undefined,
    });
  };

  const handleTemplateFilterChange = (value: TemplateType, checked: boolean) => {
    const newTemplateFilters = checked
      ? [...templateFilters, value]
      : templateFilters.filter((f) => f !== value);
    onFiltersChange({
      ...filters,
      template: newTemplateFilters.length > 0 ? newTemplateFilters : undefined,
    });
  };

  return (
    <FilterDropdownButton activeFilterCount={activeFilterCount}>
      <FilterSection>
        <FilterSectionTitle>{t('DIFFICULTY')}</FilterSectionTitle>
        {DIFFICULTY_LEVELS.map((level) => (
          <FilterOption key={level.value}>
            <input
              type="checkbox"
              checked={difficultyFilters.includes(level.value)}
              onChange={(e) => handleDifficultyFilterChange(level.value, e.target.checked)}
            />
            {t(level.label)}
          </FilterOption>
        ))}
      </FilterSection>
      <FilterSection>
        <FilterSectionTitle>{t('ITEM TYPE')}</FilterSectionTitle>
        {ITEM_TYPES.map((type) => (
          <FilterOption key={type}>
            <input
              type="checkbox"
              checked={templateFilters.includes(type)}
              onChange={(e) => handleTemplateFilterChange(type, e.target.checked)}
            />
            {t(formatItemTemplate(type))}
          </FilterOption>
        ))}
      </FilterSection>
      {activeFilterCount > 0 && (
        <ClearAllButton onClick={() => onFiltersChange({})}>{t('Clear all filters')}</ClearAllButton>
      )}
    </FilterDropdownButton>
  );
};

export const ContentIntelligenceFilterChips = ({
  filters,
  onFiltersChange,
  isLoading = false,
}: ContentIntelligenceFilterChipsProps) => {
  const { t } = useTranslation();

  const difficultyFilters = filters.difficultyLevel || [];
  const templateFilters = filters.template || [];

  const removeDifficultyFilter = (value: DifficultyLevel) => {
    const newFilters = difficultyFilters.filter((f) => f !== value);
    onFiltersChange({
      ...filters,
      difficultyLevel: newFilters.length > 0 ? newFilters : undefined,
    });
  };

  const removeTemplateFilter = (value: string) => {
    const newFilters = templateFilters.filter((f) => f !== value);
    onFiltersChange({
      ...filters,
      template: newFilters.length > 0 ? newFilters : undefined,
    });
  };

  const getDifficultyLabel = (value: DifficultyLevel): string => {
    return DIFFICULTY_LEVELS.find((d) => d.value === value)?.label || value;
  };

  const chips: FilterChipDef[] = [
    ...difficultyFilters.map((filter) => ({
      key: `difficulty-${filter}`,
      label: `${t('Difficulty')}: ${getDifficultyLabel(filter)}`,
      onDelete: () => removeDifficultyFilter(filter),
    })),
    ...templateFilters.map((filter) => ({
      key: `template-${filter}`,
      label: `${t('Type')}: ${formatItemTemplate(filter)}`,
      onDelete: () => removeTemplateFilter(filter),
    })),
  ];

  return (
    <FilterChipRow
      chips={chips}
      onClearAll={() => onFiltersChange({})}
      clearAllLabel={t('Clear all')}
      isLoading={isLoading}
    />
  );
};

const ClearAllButton = styled(TextLinkButton)`
  font-size: 13px;
  margin-top: 12px;
  padding: 0;
`;
